import React, { useState, useEffect, useRef } from "react";
import { format, parseISO, subDays, subMonths, subYears, addMonths, addYears, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear, compareAsc } from "date-fns";
import DatePicker, { registerLocale } from "react-datepicker";
import { ko } from "date-fns/esm/locale";

import "react-datepicker/dist/react-datepicker.css";
import "./style.css";
registerLocale("ko", ko);

const getDefinedPeriod = (mode, selected, selectedDate, maxDate) => {
    let start,
        end,
        yearEnd = null;
    const now = new Date();
    if (mode === "week") {
        if (selected === "current") {
            end = subDays(now, 1);
            start = startOfWeek(end, { weekStartsOn: 1 });
        } else if (selected === "last") {
            start = startOfWeek(subDays(now, 7), { weekStartsOn: 1 });
            end = endOfWeek(start, { weekStartsOn: 1 });
        } else if (selected === "selected") {
            start = startOfWeek(selectedDate, { weekStartsOn: 1 });
            end = endOfWeek(selectedDate, { weekStartsOn: 1 });
            if (compareAsc(now, end) < 0) {
                end = subDays(now, 1);
            }
        }
    } else if (mode === "month") {
        if (selected === "current") {
            end = subDays(now, 1);
            start = startOfMonth(end);
        } else if (selected === "last") {
            start = startOfMonth(subMonths(now, 1));
            end = endOfMonth(start);
        } else if (selected === "selected") {
            start = startOfMonth(selectedDate);
            end = endOfMonth(selectedDate);
            if (compareAsc(now, end) < 0) {
                end = subDays(now, 1);
            }
        }
    } else if (mode === "year") {
        if (selected === "current") {
            if (now.getMonth() + 1 < 3) {
                end = subDays(now, 1);
                start = addMonths(subYears(startOfYear(end), 1), 2);
            } else {
                end = subDays(now, 1);
                start = addMonths(startOfYear(end), 2);
            }
        } else if (selected === "last") {
            start = addMonths(startOfYear(subYears(now, now.getMonth() + 1 < 3 ? 2 : 1)), 2);
            end = addMonths(endOfYear(start), 2);
            if (compareAsc(now, end) < 0) {
                end = subDays(now, 1);
            }
        } else if (selected === "tabselected") {
            if(selectedDate.getMonth() + 1 < 3) {
                start = new Date(`${selectedDate.getFullYear() - 1}-03-01`);
            } else {
                start = new Date(`${selectedDate.getFullYear()}-03-01`);
            }
            end = subDays(addYears(start, 1), 1);
            if (compareAsc(now, end) < 0) {
                end = subDays(now, 1);
            }
        } else if (selected === "selected") {
            start = new Date(`${selectedDate.getFullYear()}-03-01`);
            end = subDays(addYears(start, 1), 1);
            if (compareAsc(now, end) < 0) {
                end = subDays(now, 1);
            }
        }
    }
    // 새학기 전인 경우 연간은 이전 년도까지만 선택 가능
    if (now.getMonth() + 1 < 3) {
        yearEnd = endOfYear(subYears(now, 1));
    } else {
        yearEnd = subDays(now, 1);
    }
    // 최대 제공 일자가 있는 경우
    if(maxDate) {
        if (compareAsc(start, new Date(maxDate)) > 0) {
            start = new Date(maxDate);
        }
        if (compareAsc(end, new Date(maxDate)) > 0) {
            end = new Date(maxDate);
        }
        if (compareAsc(yearEnd, new Date(maxDate)) > 0) {
            yearEnd = new Date(maxDate);
        }
    }

    return {
        type: mode,
        startDate: start,
        endDate: end,
        yearEndDate: yearEnd,
    };
};

const SelectStatPeriod = ({ minDate, minDateYear, maxDate, showLastYearShortCut, onPeriodChangeHandler }) => {
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [showDateRange, setShowDateRange] = useState(false);
    const [selectionMode, setSelectionMode] = useState("week");
    const [prevRange, setPrevRange] = useState(false);
    const [range, setRange] = useState({
        ...getDefinedPeriod("week", "current", null, maxDate),
        key: "selection",
    });
    const wrapperRef = useRef(null);

    function handleClickOutside(event) {
        if (wrapperRef && wrapperRef.current && !wrapperRef.current.contains(event.target)) {
            if (showDateRange) {
                setRange({ ...prevRange });
                setShowDateRange(false);
            }
        }
    }
    useEffect(() => {
        setIsFirstRender(false);

        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [range, prevRange, showDateRange, selectionMode, isFirstRender]);

    function onSelectDefinedPeriod(mode, selected, selectedDate) {
        setSelectionMode(mode);
        const newPeriod = getDefinedPeriod(mode, selected, selectedDate, maxDate);
        setRange({
            ...newPeriod,
            key: "selection",
        });
    }

    function changeShowDateRange(show, apply = false) {
        if (show === showDateRange) {
            return;
        }

        if (show) {
            setPrevRange({ ...range });
        } else {
            if (apply) {
                if (
                    onPeriodChangeHandler &&
                    (format(range.startDate, "yyyy-MM-dd") != format(prevRange.startDate, "yyyy-MM-dd") || format(range.endDate, "yyyy-MM-dd") != format(prevRange.endDate, "yyyy-MM-dd"))
                ) {
                    onPeriodChangeHandler(range.startDate, range.endDate, selectionMode);
                }
            } else {
                setRange({ ...prevRange });
            }
        }
        setShowDateRange(show);
    }

    function handleSelect(date) {
        onSelectDefinedPeriod(selectionMode, "selected", date);
    }

    return (
        <div
            style={{
                width: "100%",
                position: "relative",
                zIndex: 1,
            }}
            ref={wrapperRef}
        >
            <div className="input-group input-group-sm">
                <div className="input-group-prepend">
                    <span className="input-group-text" style={{ width: "32px" }} onClick={() => changeShowDateRange(true)}>
                        <i className="far fa-calendar-alt"></i>
                    </span>
                </div>
                <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="통계 기간"
                    value={`${format(range.startDate, "yyyy-MM-dd")} ~ ${format(range.endDate, "yyyy-MM-dd")}`}
                    onClick={() => changeShowDateRange(true)}
                    onChange={() => {}}
                />
            </div>
            {showDateRange && (
                <div className="select-stat-period">
                    <div className="row">
                        <div className="select-stat-period-predefined" style={{ width: "110px" }}>
                            <div className="list-group">
                                <button
                                    type="button"
                                    className="list-group-item list-group-item-action py-2"
                                    onClick={() => {
                                        onSelectDefinedPeriod("week", "current");
                                    }}
                                >
                                    이번 주
                                </button>
                                <button
                                    type="button"
                                    className="list-group-item list-group-item-action py-2"
                                    onClick={() => {
                                        onSelectDefinedPeriod("week", "last");
                                    }}
                                >
                                    지난 주
                                </button>
                                <button
                                    type="button"
                                    className="list-group-item list-group-item-action py-2"
                                    onClick={() => {
                                        onSelectDefinedPeriod("month", "current");
                                    }}
                                >
                                    이번 달
                                </button>
                                <button
                                    type="button"
                                    className="list-group-item list-group-item-action py-2"
                                    onClick={() => {
                                        onSelectDefinedPeriod("month", "last");
                                    }}
                                >
                                    지난 달
                                </button>
                                <button
                                    type="button"
                                    className="list-group-item list-group-item-action py-2"
                                    onClick={() => {
                                        onSelectDefinedPeriod("year", "current");
                                    }}
                                >
                                    올해 학년도
                                </button>
                                {showLastYearShortCut && (
                                    <button
                                        type="button"
                                        className="list-group-item list-group-item-action py-2"
                                        onClick={() => {
                                            onSelectDefinedPeriod("year", "last");
                                        }}
                                    >
                                        전해 학년도
                                    </button>
                                )}
                            </div>
                        </div>
                        <div style={{ width: "275px", float: "right", paddingLeft: "5px" }}>
                            <div className="btn-group btn-group-toggle w-100 mb-1" data-toggle="buttons">
                                <label
                                    style={{ width: "33%" }}
                                    className={`btn ${selectionMode === "week" ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => {
                                        onSelectDefinedPeriod("week", "selected", range.startDate);
                                    }}
                                >
                                    <input type="radio" name="options" id="option1" /> 주간
                                </label>
                                <label
                                    style={{ width: "33%" }}
                                    className={`btn ${selectionMode === "month" ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => {
                                        onSelectDefinedPeriod("month", "selected", range.startDate);
                                    }}
                                >
                                    <input type="radio" name="options" id="option2" /> 월간
                                </label>
                                <label
                                    style={{ width: "33%" }}
                                    className={`btn ${selectionMode === "year" ? "btn-primary" : "btn-outline-primary"}`}
                                    onClick={() => {
                                        onSelectDefinedPeriod("year", "tabselected", range.startDate);
                                    }}
                                >
                                    <input type="radio" name="options" id="option3" /> 연간
                                </label>
                            </div>
                            {selectionMode === "week" && (
                                <DatePicker
                                    locale={ko}
                                    onChange={(date) => handleSelect(date)}
                                    selected={range.startDate}
                                    minDate={new Date(minDate)}
                                    maxDate={maxDate ? new Date(maxDate) : subDays(new Date(), 1)}
                                    startDate={range.startDate}
                                    endDate={range.endDate}
                                    showDisabledMonthNavigation
                                    peekNextMonth
                                    showMonthDropdown
                                    showYearDropdown
                                    dropdownMode="select"
                                    inline
                                />
                            )}
                            {selectionMode === "month" && (
                                <DatePicker
                                    locale={ko}
                                    onChange={(date) => handleSelect(date)}
                                    selected={range.startDate}
                                    minDate={new Date(minDate)}
                                    maxDate={maxDate ? new Date(maxDate) : subDays(new Date(), 1)}
                                    startDate={range.startDate}
                                    endDate={range.endDate}
                                    showDisabledMonthNavigation
                                    showMonthYearPicker
                                    showFullMonthYearPicker
                                    inline
                                />
                            )}
                            {selectionMode === "year" && (
                                <DatePicker
                                    locale={ko}
                                    onChange={(date) => handleSelect(date)}
                                    selected={range.startDate}
                                    minDate={minDateYear}
                                    maxDate={range.yearEndDate}
                                    startDate={range.startDate}
                                    endDate={range.endDate}
                                    dateFormatCalendar="YYYY"
                                    showYearPicker
                                    yearItemNumber={6}
                                    inline
                                />
                            )}
                        </div>
                    </div>
                    <div className="row mt-1">
                        <div className="col text-right">
                            <button className="btn btn-outline-dark" onClick={() => changeShowDateRange(false, false)}>
                                취소
                            </button>
                        </div>
                        <div className="col text-left">
                            <button className="btn btn-primary" onClick={() => changeShowDateRange(false, true)}>
                                적용
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export { getDefinedPeriod, SelectStatPeriod };
